// Shown when a user clicks the Document Collection tab from elsewhere in
// the case workspace. Document Collection holds unreviewed, pre-production
// materials that may be privileged — the modal is a deliberate friction
// beat so it isn't entered by reflex while doing case work.
//
// Re-prompts every transition into the tab; already-on-tab clicks bypass
// the modal entirely (handled by the caller in CaseWorkspaceShell).
import { useEffect, useRef } from 'react';
import { AlertTriangleIcon } from 'lucide-react';
import { useFocusTrap } from '../hooks/shared/useFocusTrap';

// Tracks whether the backdrop click was initiated on the backdrop itself
// rather than starting inside the dialog body (e.g. mid-text-selection
// release). Without this, mousedown-inside → drag-out → mouseup-on-backdrop
// would dismiss the modal because the synthetic `click` event fires on
// the closest common ancestor (the backdrop).

interface DocumentCollectionGateModalProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function DocumentCollectionGateModal({
  open,
  onConfirm,
  onCancel,
}: DocumentCollectionGateModalProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);
  const backdropMouseDown = useRef(false);
  useFocusTrap(dialogRef, open);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onCancel]);

  if (!open) return null;

  return (
    <div
      onMouseDown={(e) => {
        backdropMouseDown.current = e.target === e.currentTarget;
      }}
      onClick={(e) => {
        const onBackdrop = e.target === e.currentTarget;
        const startedOnBackdrop = backdropMouseDown.current;
        backdropMouseDown.current = false;
        if (onBackdrop && startedOnBackdrop) onCancel();
      }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
    >
      <div
        ref={dialogRef}
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="doc-collection-gate-title"
        aria-describedby="doc-collection-gate-desc"
        className="w-full max-w-md bg-navy-light border border-rose-500/30 rounded-xl shadow-2xl overflow-hidden"
      >
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center flex-shrink-0">
              <AlertTriangleIcon className="w-5 h-5 text-rose-400" />
            </div>
            <div className="flex-1 pt-0.5">
              <h2
                id="doc-collection-gate-title"
                className="font-serif text-xl text-white mb-2 tracking-tight"
              >
                You're leaving the case workspace
              </h2>
              <p
                id="doc-collection-gate-desc"
                className="text-sm text-gray-300 leading-relaxed"
              >
                Document Collection holds unreviewed, pre-production
                materials — including documents that may be{' '}
                <span className="text-rose-300">privileged</span> or otherwise
                sensitive. Proceed only if you intend to work with intake
                materials.
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 bg-navy border-t border-navy-lighter">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-300 hover:bg-navy-lighter/60 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-rose-500/15 border border-rose-500/30 text-rose-300 hover:bg-rose-500/25 hover:text-rose-200 transition-colors"
          >
            Proceed
          </button>
        </div>
      </div>
    </div>
  );
}
